import React, { useEffect, useRef, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Save, RotateCcw, Plus, X, ExternalLink, ImageIcon, Eye, EyeOff, Upload, Loader2, Headphones, Trash, Music } from 'lucide-react';
import AiGenerateButton from '@/components/ai/AiGenerateButton';
import AiAssistantLauncher from '@/components/ai/AiAssistantLauncher';

const SECTION_LABELS = {
  headspace_feature: 'HEADSPACE Featured Course',
  mick_coach: 'MICK AI Coach Shoutout',
  featured_courses: 'Featured Courses Grid',
  pay_what_you_want: 'Pay What You Want',
  games_section: 'Games (Mini-Games Showcase)',
  song_section: 'HEADSPACE — The Song (Soundtrack)',
  my_story: "Mickey's Story",
  stats: 'Animated Stats',
  resources: 'Resources & Tools',
  community: 'Community Preview',
  testimonials: 'Testimonials',
  crisis: 'Crisis Support',
  support_page: 'Support / Tip Jar Page (/support route)',
};

export default function SplashEditor() {
  const [data, setData] = useState(null);
  const [original, setOriginal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');
  const [err, setErr] = useState('');

  const load = async () => {
    const { data: d } = await api.get('/splash');
    setData(d);
    setOriginal(JSON.stringify(d));
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setData({ ...data, [k]: v });

  const save = async () => {
    setSaving(true); setErr(''); setSavedMsg('');
    try {
      await api.put('/admin/splash', data);
      setSavedMsg('Saved. Refresh the homepage to see changes.');
      setTimeout(() => setSavedMsg(''), 4000);
      load();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm('Reset splash page to defaults? Your edits will be lost.')) return;
    await api.post('/admin/splash/reset');
    load();
  };

  const isDirty = data && original && JSON.stringify(data) !== original;

  if (!data) return <div className="text-muted-kc">Loading…</div>;

  return (
    <div data-testid="splash-editor">
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div>
          <div className="text-[11px] tracking-[0.18em] uppercase text-muted-kc">Splash Page Editor</div>
          <div className="font-display uppercase text-2xl mt-1">Homepage Content</div>
          <div className="text-sm text-muted-kc mt-1">All copy, images, and links on the public splash page. Changes apply instantly after save.</div>
        </div>
        <div className="flex items-center gap-2">
          <a href="/" target="_blank" rel="noreferrer" className="h-10 px-4 inline-flex items-center gap-1 rounded-[var(--kc-radius-sm)] text-sm font-semibold border border-kc-strong hover:bg-surface-2 transition-colors" data-testid="splash-preview"><ExternalLink size={14}/> Preview</a>
          <button onClick={reset} className="h-10 px-4 inline-flex items-center gap-1 rounded-[var(--kc-radius-sm)] text-sm font-semibold text-muted-kc hover:bg-surface-2 transition-colors" data-testid="splash-reset"><RotateCcw size={14}/> Reset</button>
          <button onClick={save} disabled={saving || !isDirty} className="h-10 px-5 inline-flex items-center gap-2 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black text-sm font-semibold hover:bg-[color:var(--kc-gold-2)] disabled:opacity-40 transition-colors" data-testid="splash-save">
            <Save size={14}/> {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
      {savedMsg && <div className="mb-4 p-3 bg-gold-soft border border-gold rounded-[var(--kc-radius-sm)] text-sm">{savedMsg}</div>}
      {err && <div className="mb-4 p-3 bg-red-50 border border-red-200 text-[color:var(--kc-danger)] rounded-[var(--kc-radius-sm)] text-sm">{err}</div>}

      <div className="space-y-6">
        <Group title="Section Visibility — Turn On/Off">
          <div className="text-sm text-muted-kc mb-3">Toggle any homepage section on or off. Changes apply instantly after Save.</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {Object.entries(SECTION_LABELS).map(([key, label]) => {
              const enabled = (data.sections_visible || {})[key] !== false;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => set('sections_visible', { ...(data.sections_visible || {}), [key]: !enabled })}
                  className={`flex items-center justify-between gap-3 p-3 rounded-[var(--kc-radius-sm)] border transition-colors ${enabled ? 'bg-gold-soft border-[color:var(--kc-gold)]' : 'bg-surface-2 border-kc'}`}
                  data-testid={`toggle-section-${key}`}
                >
                  <span className="text-sm font-semibold text-left">{label}</span>
                  <span className={`flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider ${enabled ? 'text-ink' : 'text-muted-kc'}`}>
                    {enabled ? <><Eye size={14}/> ON</> : <><EyeOff size={14}/> OFF</>}
                  </span>
                </button>
              );
            })}
          </div>
        </Group>

        <Group title="MICK AI Coach Shoutout">
          <Field label="Eyebrow" value={data.mick_eyebrow} onChange={(v) => set('mick_eyebrow', v)} testid="f-mick-eyebrow" aiField="mick_eyebrow"/>
          <Field label="Title" value={data.mick_title} onChange={(v) => set('mick_title', v)} testid="f-mick-title" aiField="mick_title"/>
          <Field label="Body" multiline value={data.mick_body} onChange={(v) => set('mick_body', v)} testid="f-mick-body" aiField="mick_body" aiLength="medium"/>
          <ListField label="Bullet features" items={data.mick_features || []} onChange={(items) => set('mick_features', items)} testid="f-mick-features"/>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="CTA label" value={data.mick_cta_label} onChange={(v) => set('mick_cta_label', v)} testid="f-mick-cta-label" aiField="mick_cta_label"/>
            <Field label="CTA link" value={data.mick_cta_href} onChange={(v) => set('mick_cta_href', v)} testid="f-mick-cta-href"/>
          </div>
        </Group>

        <Group title="Coming Soon Courses (Waitlist)">
          <div className="text-sm text-muted-kc mb-1">Each coming-soon course shows a waitlist bell that captures emails. Counts appear in the Waitlist tab.</div>
          {(data.coming_soon_courses || []).map((cs, i) => (
            <div key={i} className="bg-surface-2 p-3 rounded-[var(--kc-radius-sm)] grid grid-cols-1 md:grid-cols-12 gap-2 items-start">
              <input value={cs.slug || ''} onChange={(e) => { const x = [...data.coming_soon_courses]; x[i] = { ...x[i], slug: e.target.value }; set('coming_soon_courses', x); }} placeholder="slug" className="kc-tinput md:col-span-2" data-testid={`f-cs-slug-${i}`}/>
              <input value={cs.title || ''} onChange={(e) => { const x = [...data.coming_soon_courses]; x[i] = { ...x[i], title: e.target.value }; set('coming_soon_courses', x); }} placeholder="TITLE" className="kc-tinput md:col-span-3 uppercase font-semibold" data-testid={`f-cs-title-${i}`}/>
              <input value={cs.subtitle || ''} onChange={(e) => { const x = [...data.coming_soon_courses]; x[i] = { ...x[i], subtitle: e.target.value }; set('coming_soon_courses', x); }} placeholder="Subtitle" className="kc-tinput md:col-span-3" data-testid={`f-cs-sub-${i}`}/>
              <input value={cs.image || ''} onChange={(e) => { const x = [...data.coming_soon_courses]; x[i] = { ...x[i], image: e.target.value }; set('coming_soon_courses', x); }} placeholder="/splash/img.png" className="kc-tinput md:col-span-3" data-testid={`f-cs-img-${i}`}/>
              <button onClick={() => set('coming_soon_courses', data.coming_soon_courses.filter((_, idx) => idx !== i))} className="text-[color:var(--kc-danger)] p-2 md:col-span-1" data-testid={`f-cs-del-${i}`}><X size={14}/></button>
            </div>
          ))}
          <button onClick={() => set('coming_soon_courses', [...(data.coming_soon_courses || []), { slug: `course-${Date.now()}`, title: 'NEW COURSE', subtitle: 'Subtitle', image: '/splash/manifesto_storm.png' }])} className="text-sm font-semibold inline-flex items-center gap-1 text-gold" data-testid="f-cs-add"><Plus size={14}/> Add coming-soon course</button>
        </Group>

        <Group title="Pay What You Want">
          <Field label="Eyebrow" value={data.pwyw_eyebrow} onChange={(v) => set('pwyw_eyebrow', v)} testid="f-pwyw-eyebrow" aiField="pwyw_eyebrow"/>
          <Field label="Title" value={data.pwyw_title} onChange={(v) => set('pwyw_title', v)} testid="f-pwyw-title" aiField="pwyw_title"/>
          <Field label="Body" multiline value={data.pwyw_body} onChange={(v) => set('pwyw_body', v)} testid="f-pwyw-body" aiField="pwyw_body" aiLength="medium"/>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider mb-1">Minimum amount ($, 0 = truly free)</label>
              <input type="number" min="0" value={data.pwyw_min_amount ?? 0} onChange={(e) => set('pwyw_min_amount', Math.max(0, parseFloat(e.target.value) || 0))} className="kc-tinput" data-testid="f-pwyw-min"/>
            </div>
            <Field label="Suggested tiers (comma-separated, e.g. 0,9,27,49)" value={(data.pwyw_suggested || []).join(',')} onChange={(v) => set('pwyw_suggested', v.split(',').map(x => parseFloat(x.trim()) || 0).filter(n => !Number.isNaN(n)))} testid="f-pwyw-tiers"/>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="CTA label" value={data.pwyw_cta_label} onChange={(v) => set('pwyw_cta_label', v)} testid="f-pwyw-cta-label" aiField="pwyw_cta_label"/>
            <Field label="CTA link" value={data.pwyw_cta_href} onChange={(v) => set('pwyw_cta_href', v)} testid="f-pwyw-cta-href"/>
          </div>
          <Field label="Closing note" value={data.pwyw_note} onChange={(v) => set('pwyw_note', v)} testid="f-pwyw-note" aiField="pwyw_note"/>
        </Group>

        <Group title="Animated Stats">
          <Field label="Eyebrow" value={data.stats_eyebrow} onChange={(v) => set('stats_eyebrow', v)} testid="f-stats-eyebrow"/>
          {(data.stats_items || []).map((s, i) => (
            <div key={i} className="bg-surface-2 p-3 rounded-[var(--kc-radius-sm)] grid grid-cols-1 md:grid-cols-12 gap-2 items-start">
              <input value={s.value || ''} onChange={(e) => { const x = [...data.stats_items]; x[i] = { ...x[i], value: e.target.value }; set('stats_items', x); }} placeholder="40" className="kc-tinput md:col-span-3 font-display text-lg" data-testid={`f-stat-v-${i}`}/>
              <input value={s.suffix || ''} onChange={(e) => { const x = [...data.stats_items]; x[i] = { ...x[i], suffix: e.target.value }; set('stats_items', x); }} placeholder="+" className="kc-tinput md:col-span-2" data-testid={`f-stat-sfx-${i}`}/>
              <input value={s.label || ''} onChange={(e) => { const x = [...data.stats_items]; x[i] = { ...x[i], label: e.target.value }; set('stats_items', x); }} placeholder="Label" className="kc-tinput md:col-span-6" data-testid={`f-stat-l-${i}`}/>
              <button onClick={() => set('stats_items', data.stats_items.filter((_, idx) => idx !== i))} className="text-[color:var(--kc-danger)] p-2 md:col-span-1" data-testid={`f-stat-del-${i}`}><X size={14}/></button>
            </div>
          ))}
          <button onClick={() => set('stats_items', [...(data.stats_items || []), { value: '0', suffix: '', label: 'New stat' }])} className="text-sm font-semibold inline-flex items-center gap-1 text-gold" data-testid="f-stat-add"><Plus size={14}/> Add stat</button>
        </Group>

        <Group title="Resources & Tools Teaser">
          <Field label="Eyebrow" value={data.resources_eyebrow} onChange={(v) => set('resources_eyebrow', v)} testid="f-res-eyebrow" aiField="resources_eyebrow" aiPage="resources"/>
          <Field label="Title" value={data.resources_title} onChange={(v) => set('resources_title', v)} testid="f-res-title" aiField="resources_title" aiPage="resources"/>
          <Field label="Body" multiline value={data.resources_body} onChange={(v) => set('resources_body', v)} testid="f-res-body" aiField="resources_body" aiPage="resources" aiLength="medium"/>
        </Group>

        <Group title="Community Preview">
          <Field label="Eyebrow" value={data.community_eyebrow} onChange={(v) => set('community_eyebrow', v)} testid="f-com-eyebrow" aiField="community_eyebrow"/>
          <Field label="Title" value={data.community_title} onChange={(v) => set('community_title', v)} testid="f-com-title" aiField="community_title"/>
          <Field label="Body" multiline value={data.community_body} onChange={(v) => set('community_body', v)} testid="f-com-body" aiField="community_body" aiLength="medium"/>
        </Group>

        <Group title="Testimonials">
          {(data.testimonials || []).map((t, i) => (
            <div key={i} className="bg-surface-2 p-3 rounded-[var(--kc-radius-sm)] space-y-2 relative">
              <button onClick={() => set('testimonials', data.testimonials.filter((_, idx) => idx !== i))} className="absolute top-2 right-2 text-[color:var(--kc-danger)]" data-testid={`f-test-del-${i}`}><X size={14}/></button>
              <textarea value={t.quote || ''} onChange={(e) => { const x = [...data.testimonials]; x[i] = { ...x[i], quote: e.target.value }; set('testimonials', x); }} rows={3} placeholder='"Quote..."' className="kc-tinput" data-testid={`f-test-q-${i}`}/>
              <div className="grid grid-cols-2 gap-2">
                <input value={t.name || ''} onChange={(e) => { const x = [...data.testimonials]; x[i] = { ...x[i], name: e.target.value }; set('testimonials', x); }} placeholder="Name" className="kc-tinput" data-testid={`f-test-n-${i}`}/>
                <input value={t.label || ''} onChange={(e) => { const x = [...data.testimonials]; x[i] = { ...x[i], label: e.target.value }; set('testimonials', x); }} placeholder="Label (e.g. Course Member)" className="kc-tinput" data-testid={`f-test-l-${i}`}/>
              </div>
            </div>
          ))}
          <button onClick={() => set('testimonials', [...(data.testimonials || []), { quote: 'New testimonial...', name: 'Reader', label: 'Beta Tester' }])} className="text-sm font-semibold inline-flex items-center gap-1 text-gold" data-testid="f-test-add"><Plus size={14}/> Add testimonial</button>
        </Group>

        <Group title="Crisis Support Card">
          <Field label="Eyebrow" value={data.crisis_eyebrow} onChange={(v) => set('crisis_eyebrow', v)} testid="f-crisis-eyebrow" aiField="crisis_eyebrow"/>
          <Field label="Title" value={data.crisis_title} onChange={(v) => set('crisis_title', v)} testid="f-crisis-title" aiField="crisis_title"/>
          <Field label="Body" multiline value={data.crisis_body} onChange={(v) => set('crisis_body', v)} testid="f-crisis-body" aiField="crisis_body" aiLength="medium"/>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="Phone number" value={data.crisis_phone} onChange={(v) => set('crisis_phone', v)} testid="f-crisis-phone"/>
            <Field label="Text target" value={data.crisis_text_target} onChange={(v) => set('crisis_text_target', v)} testid="f-crisis-text"/>
            <Field label="External URL" value={data.crisis_url} onChange={(v) => set('crisis_url', v)} testid="f-crisis-url"/>
          </div>
        </Group>

        <Group title="Hero Section">
          <Field label="Eyebrow text (above title)" value={data.hero_eyebrow} onChange={(v) => set('hero_eyebrow', v)} testid="f-hero-eyebrow" aiField="hero_eyebrow"/>
          <Field label="Title — top line" value={data.hero_title_top} onChange={(v) => set('hero_title_top', v)} testid="f-hero-title-top" aiField="hero_title_top"/>
          <Field label="Title — bottom line (gold)" value={data.hero_title_bottom} onChange={(v) => set('hero_title_bottom', v)} testid="f-hero-title-bottom" aiField="hero_title_bottom"/>
          <Field label="Subtitle" multiline value={data.hero_subtitle} onChange={(v) => set('hero_subtitle', v)} testid="f-hero-subtitle" aiField="hero_subtitle" aiLength="medium"/>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Primary button label" value={data.hero_cta_primary_label} onChange={(v) => set('hero_cta_primary_label', v)} testid="f-cta1-label" aiField="hero_cta_primary_label"/>
            <Field label="Primary button link" value={data.hero_cta_primary_href} onChange={(v) => set('hero_cta_primary_href', v)} testid="f-cta1-href"/>
            <Field label="Secondary button label" value={data.hero_cta_secondary_label} onChange={(v) => set('hero_cta_secondary_label', v)} testid="f-cta2-label" aiField="hero_cta_secondary_label"/>
            <Field label="Secondary button link" value={data.hero_cta_secondary_href} onChange={(v) => set('hero_cta_secondary_href', v)} testid="f-cta2-href"/>
          </div>
          <ImageField label="Hero background image" value={data.hero_image} onChange={(v) => set('hero_image', v)} testid="f-hero-img" purpose="hero"/>
          <Field label="Hero trust line (under CTAs, near 5-star row)" value={data.hero_trust_line} onChange={(v) => set('hero_trust_line', v)} testid="f-hero-trust" aiField="hero_trust_line"/>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="bg-surface-2 p-3 rounded-[var(--kc-radius-sm)] space-y-2">
              <Field label="Stat 1 value" value={data.stat_1_value} onChange={(v) => set('stat_1_value', v)} testid="f-stat1-v"/>
              <Field label="Stat 1 label" value={data.stat_1_label} onChange={(v) => set('stat_1_label', v)} testid="f-stat1-l"/>
            </div>
            <div className="bg-surface-2 p-3 rounded-[var(--kc-radius-sm)] space-y-2">
              <Field label="Stat 2 value" value={data.stat_2_value} onChange={(v) => set('stat_2_value', v)} testid="f-stat2-v"/>
              <Field label="Stat 2 label" value={data.stat_2_label} onChange={(v) => set('stat_2_label', v)} testid="f-stat2-l"/>
            </div>
            <div className="bg-surface-2 p-3 rounded-[var(--kc-radius-sm)] space-y-2">
              <Field label="Stat 3 value" value={data.stat_3_value} onChange={(v) => set('stat_3_value', v)} testid="f-stat3-v"/>
              <Field label="Stat 3 label" value={data.stat_3_label} onChange={(v) => set('stat_3_label', v)} testid="f-stat3-l"/>
            </div>
          </div>
        </Group>

        <Group title="Scrolling Marquee">
          <ListField label="Marquee phrases (one per row)" items={data.marquee_words || []} onChange={(items) => set('marquee_words', items)} testid="f-marquee"/>
        </Group>

        <Group title="Manifesto Section">
          <Field label="Eyebrow" value={data.manifesto_eyebrow} onChange={(v) => set('manifesto_eyebrow', v)} testid="f-mf-eyebrow" aiField="manifesto_eyebrow"/>
          <Field label="Title (sentence per period)" value={data.manifesto_title} onChange={(v) => set('manifesto_title', v)} testid="f-mf-title" aiField="manifesto_title"/>
          <Field label="Body" multiline value={data.manifesto_body} onChange={(v) => set('manifesto_body', v)} testid="f-mf-body" aiField="manifesto_body" aiLength="medium"/>
          <ImageField label="Manifesto background image" value={data.manifesto_image} onChange={(v) => set('manifesto_image', v)} testid="f-mf-img" purpose="manifesto"/>
        </Group>

        <Group title="Pillars (6 cards)">
          {(data.pillars || []).map((p, i) => (
            <div key={i} className="bg-surface-2 p-3 rounded-[var(--kc-radius-sm)] flex gap-3">
              <span className="font-display text-2xl text-gold w-8 text-center flex-shrink-0">{i+1}</span>
              <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-2">
                <input value={p.title} onChange={(e) => { const x = [...data.pillars]; x[i] = { ...x[i], title: e.target.value }; set('pillars', x); }} placeholder="TITLE" className="kc-tinput uppercase font-semibold" data-testid={`f-pillar-title-${i}`}/>
                <input value={p.desc} onChange={(e) => { const x = [...data.pillars]; x[i] = { ...x[i], desc: e.target.value }; set('pillars', x); }} placeholder="Description" className="kc-tinput" data-testid={`f-pillar-desc-${i}`}/>
              </div>
              <button onClick={() => { const x = data.pillars.filter((_, idx) => idx !== i); set('pillars', x); }} className="text-[color:var(--kc-danger)] p-2" data-testid={`f-pillar-del-${i}`}><X size={14}/></button>
            </div>
          ))}
          <button onClick={() => set('pillars', [...(data.pillars || []), { title: 'NEW PILLAR', desc: 'Description' }])} className="text-sm font-semibold inline-flex items-center gap-1 text-gold" data-testid="f-pillar-add"><Plus size={14}/> Add pillar</button>
        </Group>

        <Group title="Testimonial / Quote">
          <Field label="Quote" multiline value={data.testimonial_quote} onChange={(v) => set('testimonial_quote', v)} testid="f-quote"/>
          <Field label="Attribution (name)" value={data.testimonial_attribution} onChange={(v) => set('testimonial_attribution', v)} testid="f-attribution"/>
          <Field label="Subline (small)" value={data.testimonial_subline} onChange={(v) => set('testimonial_subline', v)} testid="f-subline"/>
        </Group>

        <Group title="Final Call to Action">
          <Field label="Eyebrow" value={data.cta_eyebrow} onChange={(v) => set('cta_eyebrow', v)} testid="f-cta-eyebrow" aiField="cta_eyebrow"/>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Title — top line" value={data.cta_title_top} onChange={(v) => set('cta_title_top', v)} testid="f-cta-title-top" aiField="cta_title_top"/>
            <Field label="Title — bottom line (gold)" value={data.cta_title_bottom} onChange={(v) => set('cta_title_bottom', v)} testid="f-cta-title-bottom" aiField="cta_title_bottom"/>
          </div>
          <Field label="Body" multiline value={data.cta_body} onChange={(v) => set('cta_body', v)} testid="f-cta-body" aiField="cta_body" aiLength="medium"/>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Button label" value={data.cta_button_label} onChange={(v) => set('cta_button_label', v)} testid="f-cta-btn-label" aiField="cta_button_label"/>
            <Field label="Button link" value={data.cta_button_href} onChange={(v) => set('cta_button_href', v)} testid="f-cta-btn-href"/>
          </div>
          <ImageField label="CTA background image" value={data.cta_image} onChange={(v) => set('cta_image', v)} testid="f-cta-img" purpose="cta"/>
          <Field label="Footer motto" value={data.footer_motto} onChange={(v) => set('footer_motto', v)} testid="f-footer-motto" aiField="footer_motto"/>
        </Group>

        <Group title="HEADSPACE — The Song (Soundtrack Section)">
          <div className="text-sm text-muted-kc -mt-1 mb-2">A cinematic black section on the homepage. Spinning vinyl + click-to-play audio. Toggle the whole section on/off in the visibility list at the top.</div>
          <Field label="Eyebrow" value={data.song_eyebrow} onChange={(v) => set('song_eyebrow', v)} testid="f-song-eyebrow" aiField="song_eyebrow"/>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Title (large)" value={data.song_title} onChange={(v) => set('song_title', v)} testid="f-song-title" aiField="song_title"/>
            <Field label="Subtitle (gold, e.g. 'The Song')" value={data.song_subtitle} onChange={(v) => set('song_subtitle', v)} testid="f-song-subtitle" aiField="song_subtitle"/>
          </div>
          <Field label="Body / tagline" multiline value={data.song_body} onChange={(v) => set('song_body', v)} testid="f-song-body" aiField="song_body" aiLength="short"/>
          <Field label="Listen button label" value={data.song_button_label} onChange={(v) => set('song_button_label', v)} testid="f-song-btn-label" aiField="song_button_label"/>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider mb-1">Song audio file (MP3 / M4A / WAV)</label>
            <SongAudioUploader
              audioUrl={data.song_audio_url}
              filename={data.song_audio_filename || ''}
              onChange={(url, filename) => setData({ ...data, song_audio_url: url, song_audio_filename: filename })}
            />
          </div>
          <ImageField label="Album artwork (shows in the vinyl center on the homepage)" value={data.song_artwork_url} onChange={(v) => set('song_artwork_url', v)} testid="f-song-art" purpose="song-artwork"/>
          <Field label="Credit line (small, under the vinyl — clear to hide)" value={data.song_credit_line} onChange={(v) => set('song_credit_line', v)} testid="f-song-credit" aiField="song_credit_line"/>
          <Field label="Lyrics (optional — if filled, a 'Read lyrics' toggle appears below the player)" multiline value={data.song_lyrics} onChange={(v) => set('song_lyrics', v)} testid="f-song-lyrics" aiField="song_lyrics" aiLength="long"/>
        </Group>

        <Group title="Support / Tip Jar Page (/support)">
          <Field label="Eyebrow" value={data.support_eyebrow} onChange={(v) => set('support_eyebrow', v)} testid="f-support-eyebrow" aiField="support_eyebrow" aiPage="support"/>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Title — top" value={data.support_title_top} onChange={(v) => set('support_title_top', v)} testid="f-support-title-top" aiField="support_title_top" aiPage="support"/>
            <Field label="Title — bottom (gold)" value={data.support_title_bottom} onChange={(v) => set('support_title_bottom', v)} testid="f-support-title-bottom" aiField="support_title_bottom" aiPage="support"/>
          </div>
          <Field label="Intro (top of page — supports line breaks)" multiline value={data.support_intro} onChange={(v) => set('support_intro', v)} testid="f-support-intro" aiField="support_intro" aiPage="support" aiLength="long"/>
          <Field label="Body paragraphs (between sections)" multiline value={data.support_paragraphs} onChange={(v) => set('support_paragraphs', v)} testid="f-support-paras" aiField="support_paragraphs" aiPage="support" aiLength="long"/>
          <Field label="Amazon wishlist URL (leave blank to hide that section)" value={data.support_amazon_wishlist_url} onChange={(v) => set('support_amazon_wishlist_url', v)} testid="f-support-amazon-url"/>
          <Field label="Amazon button label" value={data.support_amazon_button_label} onChange={(v) => set('support_amazon_button_label', v)} testid="f-support-amazon-label" aiField="support_amazon_button_label" aiPage="support"/>
          <Field label="Thank-you message (bottom)" value={data.support_thanks_message} onChange={(v) => set('support_thanks_message', v)} testid="f-support-thanks" aiField="support_thanks_message" aiPage="support"/>
        </Group>
      </div>

      {/* Floating AI Design Assistant — applies suggestions directly back into fields */}
      <AiAssistantLauncher
        pageContext="Splash & Support Editor"
        currentData={data || {}}
        onApplySuggestion={(s) => {
          if (s?.field && (s.new_value || s.new_value === '')) {
            set(s.field, s.new_value);
          }
        }}
      />

      <style>{`.kc-tinput { width: 100%; padding: 0.5rem 0.75rem; border: 1px solid var(--kc-border-strong); border-radius: var(--kc-radius-sm); font-size: 0.9rem; outline: none; background: white; }
      .kc-tinput:focus { border-color: var(--kc-gold); box-shadow: 0 0 0 2px var(--kc-gold-soft); }`}</style>
    </div>
  );
}

const Group = ({ title, children }) => (
  <div className="bg-white border border-kc rounded-[var(--kc-radius-md)] p-5">
    <h3 className="font-display uppercase text-xl mb-4">{title}</h3>
    <div className="space-y-3">{children}</div>
  </div>
);

const Field = ({ label, value, onChange, multiline, testid, aiField, aiPage = 'splash', aiLength, extraContext }) => (
  <div>
    <div className="flex items-center justify-between gap-2 mb-1">
      <label className="block text-xs font-semibold uppercase tracking-wider">{label}</label>
      {aiField && (
        <AiGenerateButton
          page={aiPage}
          field={aiField}
          currentValue={value || ''}
          onApply={(text) => onChange(text)}
          length={aiLength || (multiline ? 'medium' : 'short')}
          extraContext={extraContext}
        />
      )}
    </div>
    {multiline ? (
      <textarea value={value || ''} onChange={(e) => onChange(e.target.value)} rows={3} className="kc-tinput" data-testid={testid}/>
    ) : (
      <input value={value || ''} onChange={(e) => onChange(e.target.value)} className="kc-tinput" data-testid={testid}/>
    )}
  </div>
);

const ImageField = ({ label, value, onChange, testid, purpose = 'generic' }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [showUrl, setShowUrl] = useState(false);
  const [urlDraft, setUrlDraft] = useState('');

  const previewSrc = value && !value.startsWith('http') && !value.startsWith('/splash')
    ? `${process.env.REACT_APP_BACKEND_URL || ''}${value}`
    : (value || '');

  const upload = async (file) => {
    setError('');
    if (file.size > 12 * 1024 * 1024) {
      setError(`Image is ${(file.size / (1024 * 1024)).toFixed(1)} MB. Max is 12 MB.`);
      return;
    }
    setUploading(true); setProgress(0);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('purpose', purpose);
    try {
      const r = await api.post('/admin/upload/image', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (evt) => {
          if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
        },
      });
      onChange(r.data.url);
      toast.success('Image uploaded.', { description: `${file.name} · ${(r.data.size / 1024).toFixed(0)} KB` });
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Upload failed.';
      setError(msg);
      toast.error('Image upload failed', { description: String(msg).slice(0, 200) });
    } finally {
      setUploading(false); setProgress(0);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const onPick = (e) => { const f = e.target.files?.[0]; if (f) upload(f); };
  const onDrop = (e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) upload(f); };
  const applyUrl = () => { const u = urlDraft.trim(); if (!u) return; onChange(u); setShowUrl(false); setUrlDraft(''); toast.success('URL set. Don’t forget to Save.'); };
  const removeImage = () => { if (!window.confirm('Remove this image?')) return; onChange(''); };

  return (
    <div data-testid={testid}>
      <label className="block text-xs font-semibold uppercase tracking-wider mb-1">{label}</label>
      {value ? (
        <div className="p-3 border border-[color:var(--kc-gold)] bg-gold-soft rounded-[var(--kc-radius-md)]">
          <div className="flex items-start gap-3">
            <img src={previewSrc} alt="preview" className="w-24 h-24 object-cover rounded-[var(--kc-radius-sm)] border border-kc bg-surface-2 flex-shrink-0" onError={(e) => { e.currentTarget.style.opacity = '0.4'; }}/>
            <div className="flex-1 min-w-0">
              <div className="text-xs text-muted-kc break-all leading-snug">{value}</div>
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <button type="button" onClick={() => !uploading && inputRef.current?.click()} className="text-xs font-semibold text-ink underline hover:text-[color:var(--kc-gold-2)]">Replace with upload</button>
                <span className="text-muted-kc/60 text-xs">·</span>
                <button type="button" onClick={() => { setShowUrl((v) => !v); setUrlDraft(value.startsWith('http') ? value : ''); }} className="text-xs font-semibold text-ink underline hover:text-[color:var(--kc-gold-2)]">Use a URL</button>
                <span className="text-muted-kc/60 text-xs">·</span>
                <button type="button" onClick={removeImage} className="text-xs font-semibold text-[color:var(--kc-danger)] underline">Remove</button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
            onClick={() => !uploading && inputRef.current?.click()}
            className="p-5 border-2 border-dashed border-kc-strong rounded-[var(--kc-radius-md)] text-center hover:bg-surface-2 transition-colors cursor-pointer"
          >
            {uploading ? (
              <div className="py-2">
                <Loader2 className="mx-auto mb-2 animate-spin text-[color:var(--kc-gold-2)]" size={20}/>
                <div className="text-sm font-semibold">Uploading… {progress}%</div>
                <div className="mt-2 h-1.5 w-full bg-white rounded-full overflow-hidden border border-kc">
                  <div className="h-full bg-[color:var(--kc-gold)] transition-all" style={{ width: `${progress}%` }}/>
                </div>
              </div>
            ) : (
              <>
                <ImageIcon className="mx-auto mb-1 text-muted-kc" size={20}/>
                <div className="text-sm font-semibold">Upload image</div>
                <div className="text-xs text-muted-kc mt-0.5">Drag & drop or click · PNG, JPG, WebP, GIF, SVG · max 12 MB</div>
              </>
            )}
          </div>
          <div className="text-center mt-1">
            <button type="button" onClick={() => setShowUrl((v) => !v)} className="text-xs font-semibold text-muted-kc underline hover:text-[color:var(--kc-gold-2)]">Or paste an image URL instead</button>
          </div>
        </>
      )}

      {showUrl && (
        <div className="mt-2 bg-surface-2 border border-kc rounded-[var(--kc-radius-sm)] p-3 flex flex-col sm:flex-row gap-2">
          <input value={urlDraft} onChange={(e) => setUrlDraft(e.target.value)} placeholder="/splash/your-image.png  OR  https://..." className="kc-tinput flex-1"/>
          <button type="button" onClick={applyUrl} disabled={!urlDraft.trim()} className="h-10 px-4 rounded-[var(--kc-radius-sm)] bg-ink text-white text-sm font-semibold disabled:opacity-40">Use URL</button>
          <button type="button" onClick={() => { setShowUrl(false); setUrlDraft(''); }} className="h-10 px-3 rounded-[var(--kc-radius-sm)] text-sm text-muted-kc hover:bg-white">Cancel</button>
        </div>
      )}

      <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml,image/avif,.png,.jpg,.jpeg,.webp,.gif,.svg,.avif" className="hidden" onChange={onPick}/>

      {error && <div className="mt-1 text-xs text-[color:var(--kc-danger)]">{error}</div>}
    </div>
  );
};

const ListField = ({ label, items, onChange, testid }) => {
  const [input, setInput] = useState('');
  return (
    <div data-testid={testid}>
      <label className="block text-xs font-semibold uppercase tracking-wider mb-1">{label}</label>
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={i} className="flex gap-2 items-center bg-surface-2 px-3 py-2 rounded-[var(--kc-radius-sm)] text-sm">
            <span className="flex-1 font-semibold">{it}</span>
            <button onClick={() => onChange(items.filter((_, idx) => idx !== i))} className="text-[color:var(--kc-danger)]"><X size={14}/></button>
          </div>
        ))}
        <div className="flex gap-2">
          <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (input.trim()) { onChange([...items, input.trim()]); setInput(''); }}}} placeholder="Add a phrase…" className="kc-tinput flex-1"/>
          <button onClick={() => { if (input.trim()) { onChange([...items, input.trim()]); setInput(''); } }} className="h-10 px-4 rounded-[var(--kc-radius-sm)] bg-ink text-white text-sm font-semibold">Add</button>
        </div>
      </div>
    </div>
  );
};

/* =================== SONG AUDIO UPLOADER ===================
 * Upload / replace / remove the homepage Song MP3.
 * Talks to /api/admin/splash/song-audio (POST / DELETE).
 * Streams via /api/audio/<path> for uploads; supports external URLs too.
 */
function SongAudioUploader({ audioUrl, filename, onChange }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [showUrl, setShowUrl] = useState(false);
  const [urlDraft, setUrlDraft] = useState('');

  const hasAudio = !!(audioUrl && audioUrl.trim());
  const fmtMB = (b) => (b / (1024 * 1024)).toFixed(1);

  const upload = async (file) => {
    setError('');
    if (file.size > 60 * 1024 * 1024) {
      setError(`File is ${fmtMB(file.size)} MB. Max is 60 MB.`);
      return;
    }
    setUploading(true); setProgress(0);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await api.post('/admin/splash/song-audio', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (evt) => {
          if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
        },
      });
      onChange(r.data.audio_url, r.data.audio_filename);
      toast.success('Song audio uploaded.', { description: `${file.name} · ${fmtMB(r.data.size)} MB · Auto-saved` });
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Upload failed.';
      setError(msg);
      toast.error('Upload failed', { description: String(msg).slice(0, 200) });
    } finally {
      setUploading(false); setProgress(0);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (f) upload(f);
  };

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) upload(f);
  };

  const remove = async () => {
    if (!window.confirm('Remove the current song audio? The “HEADSPACE — The Song” section will be hidden on the homepage until you upload a new file.')) return;
    try {
      await api.delete('/admin/splash/song-audio');
      onChange('', '');
      toast.success('Song audio removed.', { description: 'The Song section is now hidden on the homepage.' });
    } catch (e) {
      toast.error('Remove failed', { description: String(e?.response?.data?.detail || e.message).slice(0, 200) });
    }
  };

  const applyUrl = () => {
    const u = urlDraft.trim();
    if (!u) return;
    onChange(u, '');
    setShowUrl(false);
    setUrlDraft('');
    toast.success('External audio URL set. Don’t forget to Save.');
  };

  const audioSrc = audioUrl?.startsWith('http')
    ? audioUrl
    : `${process.env.REACT_APP_BACKEND_URL || ''}${audioUrl || ''}`;

  return (
    <div className="space-y-2" data-testid="song-audio-uploader">
      {hasAudio ? (
        <div className="p-4 border border-[color:var(--kc-gold)] bg-gold-soft rounded-[var(--kc-radius-md)]" data-testid="song-audio-current">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-ink flex items-center justify-center flex-shrink-0">
              <Music size={16} className="text-[color:var(--kc-gold)]"/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate">{filename || (audioUrl.startsWith('http') ? 'External song URL' : 'Uploaded song')}</div>
              <div className="text-xs text-muted-kc truncate">{audioUrl}</div>
            </div>
            <button
              type="button"
              onClick={remove}
              className="h-9 px-3 inline-flex items-center gap-1 rounded-[var(--kc-radius-sm)] text-[color:var(--kc-danger)] text-xs hover:bg-red-50 font-semibold"
              data-testid="song-audio-remove"
            >
              <Trash size={12}/> Remove
            </button>
          </div>
          <audio controls preload="none" src={audioSrc} className="mt-3 w-full" data-testid="song-audio-preview"/>
          <div className="mt-3 flex flex-wrap items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => !uploading && inputRef.current?.click()}
              className="text-xs font-semibold text-ink underline hover:text-[color:var(--kc-gold-2)]"
              data-testid="song-audio-replace"
            >
              Replace with a new file
            </button>
            <span className="text-muted-kc/60 text-xs">·</span>
            <button
              type="button"
              onClick={() => { setShowUrl((v) => !v); setUrlDraft(audioUrl?.startsWith('http') ? audioUrl : ''); }}
              className="text-xs font-semibold text-ink underline hover:text-[color:var(--kc-gold-2)]"
              data-testid="song-audio-url-toggle"
            >
              Or paste an external URL
            </button>
          </div>
        </div>
      ) : (
        <>
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
            onClick={() => !uploading && inputRef.current?.click()}
            className="p-6 border-2 border-dashed border-kc-strong rounded-[var(--kc-radius-md)] text-center hover:bg-surface-2 transition-colors cursor-pointer"
            data-testid="song-audio-drop"
          >
            {uploading ? (
              <div className="py-3">
                <Loader2 className="mx-auto mb-2 animate-spin text-[color:var(--kc-gold-2)]" size={24}/>
                <div className="text-sm font-semibold">Uploading… {progress}%</div>
                <div className="mt-2 h-1.5 w-full bg-white rounded-full overflow-hidden border border-kc">
                  <div className="h-full bg-[color:var(--kc-gold)] transition-all" style={{ width: `${progress}%` }}/>
                </div>
              </div>
            ) : (
              <>
                <Upload className="mx-auto mb-2 text-muted-kc" size={24}/>
                <div className="text-sm font-semibold">Upload your song</div>
                <div className="text-xs text-muted-kc mt-1">Drag & drop or click to pick · MP3, M4A, WAV, OGG · max 60 MB</div>
                <div className="text-[10px] text-muted-kc mt-2 uppercase tracking-wider">Stored securely · auto-replaces previous file</div>
              </>
            )}
          </div>
          <div className="text-center">
            <button
              type="button"
              onClick={() => setShowUrl((v) => !v)}
              className="text-xs font-semibold text-muted-kc underline hover:text-[color:var(--kc-gold-2)]"
              data-testid="song-audio-url-toggle-empty"
            >
              Or paste an external URL instead
            </button>
          </div>
        </>
      )}

      {showUrl && (
        <div className="bg-surface-2 border border-kc rounded-[var(--kc-radius-sm)] p-3 flex flex-col sm:flex-row gap-2" data-testid="song-audio-url-row">
          <input
            value={urlDraft}
            onChange={(e) => setUrlDraft(e.target.value)}
            placeholder="https://example.com/song.mp3"
            className="kc-tinput flex-1"
            data-testid="song-audio-url-input"
          />
          <button
            type="button"
            onClick={applyUrl}
            disabled={!urlDraft.trim()}
            className="h-10 px-4 rounded-[var(--kc-radius-sm)] bg-ink text-white text-sm font-semibold disabled:opacity-40"
            data-testid="song-audio-url-apply"
          >
            Use this URL
          </button>
          <button
            type="button"
            onClick={() => { setShowUrl(false); setUrlDraft(''); }}
            className="h-10 px-3 rounded-[var(--kc-radius-sm)] text-sm text-muted-kc hover:bg-white"
          >
            Cancel
          </button>
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="audio/mpeg,audio/mp4,audio/wav,audio/ogg,audio/webm,audio/aac,audio/flac,audio/*,.mp3,.m4a,.wav,.ogg,.webm,.aac,.flac"
        className="hidden"
        onChange={onPick}
        data-testid="song-audio-input"
      />

      {error && <div className="text-xs text-[color:var(--kc-danger)]" data-testid="song-audio-error">{error}</div>}
    </div>
  );
}
